import { CircleAlert, MapPin, RefreshCw, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ButtonLabel } from "../../components/ButtonLabel";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { currentPosition } from "../../lib/geo";
import { formatTimestamp } from "../../lib/format";
import { mxn } from "../../lib/reservations";
import { Feedback, Pill, StatTile, useAction } from "./shared";

/**
 * "Ahorradores": everyone saving for this trip, how far along each Apartado is, and Redeem — pay
 * the fully saved ones to Aventuria straight from each customer's Kuspit contract (fund units are
 * sold, then a transfer to Aventuria's treasury account, registered as beneficiary on every
 * contract). Kuspit requires the real place the instruction is given, so Redeem asks the browser
 * for the administrator's location.
 */

type PaymentStatus = "queued" | "selling" | "ready" | "sending" | "processing" | "settled" | "failed" | "manual_review";

interface Saver {
  apartadoId: string;
  apartadoStatus: string;
  customer: { id: string; name: string; email: string };
  folio: string;
  requestStatus: string;
  departureDate: string | null;
  contract: string | null;
  goal: number;
  balance: number;
  applied: number;
  progress: number;
  fullySaved: boolean;
  supplier: { status: string; usableFrom: string | null; error: string | null };
  payment: null | {
    id: string; status: PaymentStatus; amount: number; transferredAmount: number | null; feeAmount: number;
    trackingKey: string | null; failureCode: string | null; createdAt: string; settledAt: string | null;
  };
  redeemable: boolean;
}

interface SaversPayload {
  treasuryConfigured: boolean;
  savers: Saver[];
  totals: { savers: number; fullySaved: number; saved: number; goal: number; paid: number };
}

interface RedeemResult { apartadoId: string; outcome: "queued" | "rejected"; code?: string; message?: string; amount?: number }

const PAYMENT_LABEL: Record<PaymentStatus, { label: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  queued: { label: "En cola", tone: "info" },
  selling: { label: "Vendiendo fondo", tone: "info" },
  ready: { label: "Por transferir", tone: "info" },
  sending: { label: "Enviando", tone: "warn" },
  processing: { label: "Transferencia registrada", tone: "warn" },
  settled: { label: "Pagado a Aventuria", tone: "ok" },
  failed: { label: "Falló", tone: "bad" },
  manual_review: { label: "Requiere revisión", tone: "bad" }
};

const SUPPLIER_LABEL: Record<string, { label: string; tone: "ok" | "warn" | "bad" | "muted" }> = {
  registered: { label: "Beneficiario listo", tone: "ok" },
  failed: { label: "Registro pendiente", tone: "warn" },
  missing: { label: "Sin registrar", tone: "warn" },
  needs_review: { label: "Registro en revisión", tone: "bad" },
  not_configured: { label: "Tesorería sin configurar", tone: "muted" }
};

export function AdminTripSavers({ tripId }: { tripId: string }) {
  const queryClient = useQueryClient();
  const key = ["admin", "trip-savers", tripId];
  const data = useQuery({
    queryKey: key,
    queryFn: () => apiRequest<{ data: SaversPayload }>(`/api/v1/admin/trips/${tripId}/savers`),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
  const action = useAction();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<RedeemResult[] | null>(null);
  const [confirming, setConfirming] = useState(false);

  const savers = data.data?.savers ?? [];
  const redeemable = useMemo(() => savers.filter((saver) => saver.redeemable), [savers]);
  const chosen = savers.filter((saver) => selected.has(saver.apartadoId) && saver.redeemable);
  const chosenTotal = Math.round(chosen.reduce((sum, saver) => sum + saver.balance, 0) * 100) / 100;
  const toggle = (id: string) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allChosen = redeemable.length > 0 && redeemable.every((saver) => selected.has(saver.apartadoId));

  const redeem = () => action.run("redeem", async () => {
    const location = await currentPosition();
    const response = await apiRequest<{ data: { results: RedeemResult[] } }>(`/api/v1/admin/trips/${tripId}/redeem`, {
      method: "POST", body: JSON.stringify({ apartadoIds: chosen.map((saver) => saver.apartadoId), ...location })
    });
    setResults(response.data.results);
    setSelected(new Set());
    setConfirming(false);
    await queryClient.invalidateQueries({ queryKey: key });
    const queued = response.data.results.filter((result) => result.outcome === "queued").length;
    return `${queued} de ${response.data.results.length} pagos en camino. Se venden los fondos y se transfiere a Aventuria en horario de Kuspit.`;
  });

  const retry = (paymentId: string) => action.run(`retry-${paymentId}`, async () => {
    await apiRequest(`/api/v1/admin/trip-payments/${paymentId}/retry`, { method: "POST", body: "{}" });
    await queryClient.invalidateQueries({ queryKey: key });
    return "El pago se reintentará con una nueva clave de rastreo.";
  });

  if (data.isLoading) return <div className="page-state"><Spinner label="Cargando ahorradores" /></div>;
  if (data.isError || !data.data) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No pudimos cargar los ahorradores</strong></div>;
  const totals = data.data.totals;
  const nameOf = (id: string) => savers.find((saver) => saver.apartadoId === id)?.customer.name ?? id;

  return (
    <div className="admin-stack">
      <div className="stat-grid">
        <StatTile label="Ahorradores" value={<span translate="no">{totals.savers}</span>} hint="Apartados abiertos para este viaje" compact />
        <StatTile label="Meta completa" value={<span translate="no">{totals.fullySaved}</span>} hint="listos para pagar" compact tone={totals.fullySaved > 0 ? "ok" : "default"} />
        <StatTile label="Apartado" value={<span translate="no">{mxn(totals.saved)}</span>} hint={<>de <span translate="no">{mxn(totals.goal)}</span> en metas</>} compact />
        <StatTile label="Pagado a Aventuria" value={<span translate="no">{mxn(totals.paid)}</span>} hint="transferencias liquidadas" compact />
      </div>

      {!data.data.treasuryConfigured && (
        <div className="form-error" role="alert">La cuenta de tesorería de Aventuria no está configurada (AVENTURIA_TREASURY_*). No se puede registrar el beneficiario ni pagar viajes.</div>
      )}
      <Feedback error={action.error} notice={action.notice} />
      {results && results.some((result) => result.outcome === "rejected") && (
        <div className="form-error" role="alert">
          <ul className="form-error-list">
            {results.filter((result) => result.outcome === "rejected").map((result) => <li key={result.apartadoId}>{nameOf(result.apartadoId)}: {result.message}</li>)}
          </ul>
        </div>
      )}

      <div className="discover-toolbar" style={{ justifyContent: "space-between" }}>
        <span className="request-meta">
          {chosen.length > 0
            ? <>{chosen.length} seleccionados · <strong translate="no">{mxn(chosenTotal)}</strong></>
            : <>Selecciona los Apartados con meta completa para pagarlos a Aventuria.</>}
        </span>
        {confirming ? (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className="request-meta"><MapPin size={14} aria-hidden="true" /> Se enviará tu ubicación a Kuspit.</span>
            <button type="button" className="secondary-button" onClick={() => setConfirming(false)} disabled={action.busy === "redeem"}>Cancelar</button>
            <button type="button" className="primary-button" onClick={() => void redeem()} disabled={action.busy === "redeem"}>
              <ButtonLabel busy={action.busy === "redeem"} busyLabel="Enviando" label="Confirmar pago" />
            </button>
          </span>
        ) : (
          <button type="button" className="primary-button" disabled={chosen.length === 0} onClick={() => { action.clear(); setResults(null); setConfirming(true); }}>
            <Send size={16} aria-hidden="true" /> Redeem
          </button>
        )}
      </div>

      <div className="table-wrap card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th><input type="checkbox" aria-label="Seleccionar todos los listos" checked={allChosen} disabled={redeemable.length === 0} onChange={() => setSelected(allChosen ? new Set() : new Set(redeemable.map((saver) => saver.apartadoId)))} /></th>
              <th>Cliente</th><th>Solicitud</th><th>Contrato</th><th>Apartado</th><th>Meta</th><th>Beneficiario</th><th>Pago</th><th />
            </tr>
          </thead>
          <tbody>
            {savers.length === 0 && <tr><td colSpan={9} className="table-empty">Nadie está ahorrando para este viaje todavía.</td></tr>}
            {savers.map((saver) => {
              const supplier = SUPPLIER_LABEL[saver.supplier.status] ?? { label: saver.supplier.status, tone: "muted" as const };
              const payment = saver.payment;
              return (
                <tr key={saver.apartadoId}>
                  <td><input type="checkbox" aria-label={`Seleccionar ${saver.customer.name}`} checked={selected.has(saver.apartadoId)} disabled={!saver.redeemable} onChange={() => toggle(saver.apartadoId)} /></td>
                  <td>{saver.customer.name}<br /><span className="request-meta">{saver.customer.email}</span></td>
                  <td translate="no">{saver.folio}{saver.departureDate && <><br /><span className="request-meta">sale {saver.departureDate}</span></>}</td>
                  <td translate="no">{saver.contract ?? "—"}</td>
                  <td translate="no"><strong>{mxn(saver.balance)}</strong>{saver.applied > 0 && <><br /><span className="request-meta">aplicado {mxn(saver.applied)}</span></>}</td>
                  <td>
                    <span translate="no">{mxn(saver.goal)} · {Math.round(saver.progress * 100)}%</span><br />
                    {saver.fullySaved ? <Pill label="Meta completa" tone="ok" /> : <Pill label="Ahorrando" tone="muted" />}
                  </td>
                  <td><span title={saver.supplier.error ?? undefined}><Pill label={supplier.label} tone={supplier.tone} /></span></td>
                  <td>
                    {payment ? (
                      <>
                        <Pill label={PAYMENT_LABEL[payment.status].label} tone={PAYMENT_LABEL[payment.status].tone} />
                        <br />
                        <span className="request-meta" translate="no">
                          {mxn(payment.transferredAmount ?? payment.amount)}
                          {payment.feeAmount > 0 && <> · comisión {mxn(payment.feeAmount)}</>}
                        </span>
                        {payment.trackingKey && <><br /><span className="request-meta" translate="no">{payment.trackingKey}</span></>}
                        {payment.failureCode && payment.status !== "settled" && <><br /><span className="request-meta">{payment.failureCode}</span></>}
                        <br /><span className="request-meta" translate="no">{formatTimestamp(payment.settledAt ?? payment.createdAt)}</span>
                      </>
                    ) : <span className="request-meta">—</span>}
                  </td>
                  <td>
                    {payment && (payment.status === "failed" || payment.status === "manual_review") && (
                      <button type="button" className="secondary-button small" onClick={() => void retry(payment.id)} disabled={action.busy === `retry-${payment.id}`}>
                        <ButtonLabel busy={action.busy === `retry-${payment.id}`} busyLabel="Reintentando" label="Reintentar" icon={<RefreshCw size={14} aria-hidden="true" />} />
                      </button>
                    )}
                    <Link className="secondary-button small" to={`/admin/apartados/${saver.apartadoId}`}>Abrir</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
